import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Card, List, ListItem, ListItemPrefix, Typography } from "@material-tailwind/react";
import CustomAccordion from "components/Accordion/CustomAccordion";
import { motion } from "framer-motion";
import React, { FC, useEffect, useState } from "react";
import { MdDashboardCustomize } from "react-icons/md";
import { Link, useLocation } from "react-router-dom";
import { menuConfig } from "./menuConfig";

export interface DashboardMenuProps {
  children?: React.ReactNode;
}

const DashboardMenu: FC<DashboardMenuProps> = ({ children }) => {
  const [open, setOpen] = useState<number>(0);
  const location = useLocation();
  const [activeSubItem, setActiveSubItem] = useState<string | null>(location.pathname);

  const handleSubItemClick = (path: string) => {
    setActiveSubItem(path);
  };

  const handleOpen = (value: number) => {
    setOpen(open === value ? 0 : value);
  };

  useEffect(() => {
    setActiveSubItem(location.pathname);
    const activeMenu = menuConfig.find((menu: any) =>
      menu.subItems?.some((item: any) => item.to === location.pathname)
    );
    setOpen(activeMenu ? activeMenu.id : 0);
  }, [location]);

  return (
    <div className="flex flex-col min-h-screen lg:flex-row bg-neutral-50 dark:bg-neutral-900">
      <motion.div
        transition={{ duration: 1 }}
        className="p-4 border-r shadow-lg lg:p-6 lg:w-80 border-neutral-200 dark:border-neutral-700 dark:bg-gray-800"
      >
        <Card className="w-full h-full p-4 overflow-auto dark:bg-gray-800 scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-200">
          <div className="p-4 mb-4">
            <Typography variant="h5" color="blue-gray" className="dark:text-white">
              Bienvenue
            </Typography>
          </div>
          <List>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <ListItem
                className={`p-0 ${location.pathname === "/dashboard" ? "font-bold bg-blue-50 dark:bg-blue-900" : ""}`}
              >
                <Link to="/dashboard" className="flex items-center w-full p-3 dark:text-white">
                  <ListItemPrefix>
                    <MdDashboardCustomize className="w-6 h-6 mr-2" />
                  </ListItemPrefix>
                  Tableau de bord
                </Link>
              </ListItem>
            </motion.div>
            {menuConfig.map((menu: any) =>
              menu.subItems ? (
                <CustomAccordion
                  key={menu.id}
                  open={open === menu.id}
                  handleOpen={() => handleOpen(menu.id)}
                  title={menu.title}
                  icon={menu.icon}
                  subItems={menu.subItems}
                  customIcon={
                    <ChevronDownIcon
                      className={`h-4 w-4 transition-transform dark:text-white ${open === menu.id ? "rotate-180" : ""}`}
                    />
                  }
                  activeSubItem={activeSubItem}
                  handleSubItemClick={handleSubItemClick}
                />
              ) : (
                <motion.div key={menu.id} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <ListItem
                    className={`p-0 ${location.pathname === menu.to ? "font-bold bg-blue-50 dark:bg-blue-900" : ""}`}
                  >
                    <Link to={menu.to!} className="flex items-center w-full p-3 dark:text-white">
                      <ListItemPrefix>{menu.icon}</ListItemPrefix>
                      {menu.title}
                    </Link>
                  </ListItem>
                </motion.div>
              )
            )}
          </List>
        </Card>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="flex-1 p-4 lg:p-8"
      >
        {children}
      </motion.div>
    </div>
  );
};

export default DashboardMenu;
